import React, { useCallback, useMemo, useState } from 'react';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { Dialog } from '@headlessui/react';
import { Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { EmptyObject } from 'libs/end-to-end/src/generic';
import { TextInput } from 'apps/sigma2-ui/src/components/ui/TextInput';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { useCrudOperations } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/use-crud-operations';
import { ExternalDagsterTransform } from 'libs/end-to-end/src/external-dagster-transforms';
import { ExternalDagsterTransforms } from 'libs/end-to-end/src/external-dagster-transforms';
import { CreateExternalDagsterTransformRequest } from 'libs/end-to-end/src/external-dagster-transforms';
import { UpdateExternalDagsterTransformRequest } from 'libs/end-to-end/src/external-dagster-transforms';
import { OrganizationExternalDagsterTransformEntitlement } from 'libs/end-to-end/src/external-dagster-transforms';
import { OrganizationExternalDagsterTransformEntitlements } from 'libs/end-to-end/src/external-dagster-transforms';
import { GrantOrganizationDagsterTransformAccessRequest } from 'libs/end-to-end/src/external-dagster-transforms';
import { Organizations } from 'libs/end-to-end/src/types/organizations/organizations';
import { uiAppLogger } from 'apps/sigma2-ui/src/lib/logging';

//eslint-disable-next-line complexity
export const ExternalDagsterTransformsPage = () => {
  const { userAuthenticatedApiCall } = useUser();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isGrantAccessModalOpen, setIsGrantAccessModalOpen] = useState(false);
  const [editingTransform, setEditingTransform] =
    useState<ExternalDagsterTransform | null>(null);
  const [selectedTransformForAccess, setSelectedTransformForAccess] =
    useState<ExternalDagsterTransform | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isGranting, setIsGranting] = useState(false);

  const [name, setName] = useState('');
  const [database, setDatabase] = useState('');
  const [schema, setSchema] = useState('');
  const [tableName, setTableName] = useState('');
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('');

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [localTransforms, setLocalTransforms] = useState<
    ExternalDagsterTransform[]
  >([]);
  const [localEntitlements, setLocalEntitlements] = useState<
    OrganizationExternalDagsterTransformEntitlement[]
  >([]);

  const getTransformsUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/external-dagster-transforms`;
  }, []);

  const transformsReadPattern = useSimpleReadPattern({
    getUrlFn: getTransformsUrlFn,
    canRead: true,
    responseSchema: ExternalDagsterTransforms,
  });

  const getOrganizationsUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations`;
  }, []);

  const organizationsReadPattern = useSimpleReadPattern({
    getUrlFn: getOrganizationsUrlFn,
    canRead: true,
    responseSchema: Organizations,
  });

  React.useEffect(() => {
    if (
      transformsReadPattern.status === 'done' &&
      'entity' in transformsReadPattern
    ) {
      setLocalTransforms(transformsReadPattern.entity);
    }
  }, [transformsReadPattern.status, transformsReadPattern]);

  const crudOps = useCrudOperations({
    items: localTransforms,
    setItems: setLocalTransforms,
  });

  const refreshTransforms = useCallback(() => {
    if (
      transformsReadPattern.status === 'done' ||
      transformsReadPattern.status === 'error'
    ) {
      transformsReadPattern.reloadEntity();
    }
  }, [transformsReadPattern]);

  const refreshEntitlements = useCallback(
    async (transformId: string) => {
      const result = await userAuthenticatedApiCall({
        responseSchema: OrganizationExternalDagsterTransformEntitlements,
        url: `${getApiBaseUrl()}/external-dagster-transforms/${transformId}/entitlements`,
        requestOptions: {
          method: 'GET',
        },
      });

      if (result && result.resultType === 'ok') {
        setLocalEntitlements(result.value);
      }
    },
    [userAuthenticatedApiCall],
  );

  const resetForm = useCallback(() => {
    setName('');
    setDatabase('');
    setSchema('');
    setTableName('');
    setSelectedOrganizationId('');
    setEditingTransform(null);
    setSelectedTransformForAccess(null);
  }, []);

  const openCreateModal = useCallback(() => {
    resetForm();
    setIsCreateModalOpen(true);
  }, [resetForm]);

  const openEditModal = useCallback((transform: ExternalDagsterTransform) => {
    setEditingTransform(transform);
    setName(transform.name);
    setDatabase(transform.database);
    setSchema(transform.schema);
    setTableName(transform.tableName);
    setIsEditModalOpen(true);
  }, []);

  const openGrantAccessModal = useCallback(
    async (transform: ExternalDagsterTransform) => {
      setSelectedTransformForAccess(transform);
      setSelectedOrganizationId('');
      setIsGrantAccessModalOpen(true);
      setLocalEntitlements([]);
      await refreshEntitlements(transform.id);
    },
    [refreshEntitlements],
  );

  const closeModals = useCallback(() => {
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setIsGrantAccessModalOpen(false);
    resetForm();
  }, [resetForm]);

  const createTransform = useAsyncCallback(
    async (isAlive) => {
      if (!isAlive()) return;

      setIsCreating(true);
      setErrorMessage(null);

      try {
        const request: CreateExternalDagsterTransformRequest = {
          name,
          database,
          schema,
          tableName,
        };

        const result = await userAuthenticatedApiCall({
          responseSchema: ExternalDagsterTransform,
          url: `${getApiBaseUrl()}/external-dagster-transforms`,
          requestOptions: {
            method: 'POST',
            body: JSON.stringify(request),
            headers: {
              'Content-Type': 'application/json',
            },
          },
        });

        if (!result) {
          throw new Error('Failed to create dagster transform');
        }

        if (result.resultType === 'err') {
          throw new Error(
            (result.error.errorReason as string) ||
              'Failed to create dagster transform',
          );
        }

        crudOps.onAdd(result.value);
        refreshTransforms();
        closeModals();
      } catch (error) {
        setErrorMessage(
          error instanceof Error
            ? error.message
            : 'Failed to create dagster transform',
        );
      } finally {
        setIsCreating(false);
      }
    },
    [
      name,
      tableName,
      userAuthenticatedApiCall,
      refreshTransforms,
      closeModals,
      crudOps,
    ],
  );

  const updateTransform = useAsyncCallback(
    async (isAlive) => {
      if (!isAlive() || !editingTransform) return;

      setIsUpdating(true);
      setErrorMessage(null);

      try {
        const request: UpdateExternalDagsterTransformRequest = {
          name,
          database,
          schema,
          tableName,
        };

        const result = await userAuthenticatedApiCall({
          responseSchema: ExternalDagsterTransform,
          url: `${getApiBaseUrl()}/external-dagster-transforms/${editingTransform.id}`,
          requestOptions: {
            method: 'PUT',
            body: JSON.stringify(request),
            headers: {
              'Content-Type': 'application/json',
            },
          },
        });

        if (!result) {
          throw new Error('Failed to update dagster transform');
        }

        if (result.resultType === 'err') {
          throw new Error(
            (result.error.errorReason as string) ||
              'Failed to update dagster transform',
          );
        }

        const index = localTransforms.findIndex(
          (t) => t.id === editingTransform.id,
        );
        if (index !== -1) {
          crudOps.onEdit(index, result.value);
        }
        refreshTransforms();
        closeModals();
      } catch (error) {
        setErrorMessage(
          error instanceof Error
            ? error.message
            : 'Failed to update dagster transform',
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [
      editingTransform,
      name,
      database,
      schema,
      tableName,
      userAuthenticatedApiCall,
      refreshTransforms,
      closeModals,
      localTransforms,
      crudOps,
    ],
  );

  const deleteTransform = useAsyncCallback(
    async (isAlive, transform: ExternalDagsterTransform) => {
      if (!isAlive()) return;

      if (!confirm(`Are you sure you want to delete "${transform.name}"?`)) {
        return;
      }

      try {
        const result = await userAuthenticatedApiCall({
          responseSchema: EmptyObject,
          url: `${getApiBaseUrl()}/external-dagster-transforms/${transform.id}`,
          requestOptions: {
            method: 'DELETE',
          },
        });

        if (!result) {
          throw new Error('Failed to delete dagster transform');
        }

        if (result.resultType === 'err') {
          throw new Error(
            (result.error.errorReason as string) ||
              'Failed to delete dagster transform',
          );
        }

        refreshTransforms();
      } catch (error) {
        setErrorMessage(
          error instanceof Error
            ? error.message
            : 'Failed to delete dagster transform',
        );
      }
    },
    [userAuthenticatedApiCall, refreshTransforms],
  );

  const grantAccess = useAsyncCallback(
    async (isAlive) => {
      if (!isAlive() || !selectedTransformForAccess) return;

      setIsGranting(true);
      setErrorMessage(null);

      try {
        const request: GrantOrganizationDagsterTransformAccessRequest = {
          organizationId: selectedOrganizationId,
          dagsterTransformId: selectedTransformForAccess.id,
        };

        const result = await userAuthenticatedApiCall({
          responseSchema: OrganizationExternalDagsterTransformEntitlement,
          url: `${getApiBaseUrl()}/external-dagster-transforms/grant-access`,
          requestOptions: {
            method: 'POST',
            body: JSON.stringify(request),
            headers: {
              'Content-Type': 'application/json',
            },
          },
        });

        if (!result) {
          throw new Error('Failed to grant access');
        }

        if (result.resultType === 'err') {
          throw new Error(
            (result.error.errorReason as string) || 'Failed to grant access',
          );
        }

        await refreshEntitlements(selectedTransformForAccess.id);
        setSelectedOrganizationId('');
      } catch (error) {
        setErrorMessage(
          error instanceof Error ? error.message : 'Failed to grant access',
        );
      } finally {
        setIsGranting(false);
      }
    },
    [
      selectedTransformForAccess,
      selectedOrganizationId,
      userAuthenticatedApiCall,
      refreshEntitlements,
    ],
  );

  const revokeAccess = useAsyncCallback(
    async (
      isAlive,
      entitlement: OrganizationExternalDagsterTransformEntitlement,
    ) => {
      if (!isAlive() || !selectedTransformForAccess) return;

      if (!confirm('Are you sure you want to revoke access?')) {
        return;
      }

      try {
        const result = await userAuthenticatedApiCall({
          responseSchema: EmptyObject,
          url: `${getApiBaseUrl()}/external-dagster-transforms/revoke-access?organizationId=${entitlement.organizationId}&dagsterTransformId=${entitlement.dagsterTransformId}`,
          requestOptions: {
            method: 'POST',
          },
        });

        if (!result) {
          throw new Error('Failed to revoke access');
        }

        if (result.resultType === 'err') {
          throw new Error(
            (result.error.errorReason as string) || 'Failed to revoke access',
          );
        }

        await refreshEntitlements(selectedTransformForAccess.id);
      } catch (error) {
        setErrorMessage(
          error instanceof Error ? error.message : 'Failed to revoke access',
        );
      }
    },
    [selectedTransformForAccess, userAuthenticatedApiCall, refreshEntitlements],
  );

  const getOrganizationName = useMemo(() => {
    if (
      organizationsReadPattern.status !== 'done' ||
      !('entity' in organizationsReadPattern)
    ) {
      return (id: string) => id;
    }

    const orgs = organizationsReadPattern.entity.organizations;
    return (id: string) => {
      const org = orgs.find((o) => o.id === id);
      return org?.name || id;
    };
  }, [organizationsReadPattern]);

  const columnDefinitions = useMemo(
    () => [
      {
        id: 'name',
        header: () => <div>Name</div>,
        cell: ({ item }: { item: ExternalDagsterTransform }) => (
          <div className="font-medium">{item.name}</div>
        ),
      },
      {
        id: 'fullyQualifiedName',
        header: () => <div>Fully Qualified Name</div>,
        cell: ({ item }: { item: ExternalDagsterTransform }) => (
          <div className="text-gray-600 font-mono text-sm">
            {item.database}.{item.schema}.{item.tableName}
          </div>
        ),
      },
      {
        id: 'created',
        header: () => <div>Created</div>,
        cell: ({ item }: { item: ExternalDagsterTransform }) => (
          <div className="text-sm text-gray-500">
            {new Date(item.createdAt).toLocaleDateString()}
          </div>
        ),
      },
      {
        id: 'actions',
        header: () => <div>Actions</div>,
        cell: ({ item }: { item: ExternalDagsterTransform }) => (
          <div className="flex gap-2">
            <Button
              behavior={{
                type: 'onClick',
                onClick: () => openEditModal(item),
              }}
              text="Edit"
              size="small"
            />
            <Button
              behavior={{
                type: 'onClick',
                onClick: () => openGrantAccessModal(item),
              }}
              text="Manage Access"
              size="small"
            />
            <Button
              behavior={{
                type: 'onClick',
                onClick: () => deleteTransform(item),
              }}
              text="Delete"
              size="small"
              variant="outline"
            />
          </div>
        ),
      },
    ],
    [openEditModal, openGrantAccessModal, deleteTransform],
  );

  const entitlementsColumnDefinitions = useMemo(
    () => [
      {
        id: 'organization',
        header: () => <div>Organization</div>,
        cell: ({
          item,
        }: {
          item: OrganizationExternalDagsterTransformEntitlement;
        }) => <div>{getOrganizationName(item.organizationId)}</div>,
      },
      {
        id: 'actions',
        header: () => <div>Actions</div>,
        cell: ({
          item,
        }: {
          item: OrganizationExternalDagsterTransformEntitlement;
        }) => (
          <Button
            behavior={{
              type: 'onClick',
              onClick: () => revokeAccess(item),
            }}
            text="Revoke Access"
            size="small"
            variant="outline"
          />
        ),
      },
    ],
    [getOrganizationName, revokeAccess],
  );

  return (
    <AdminLayout currentPage={'ExternalDagsterTransforms'}>
      <AdminBreadcrumbs
        items={[
          { label: 'Home', to: '/admin' },
          {
            label: 'Dagster Transforms',
            to: '/external-dagster-transforms',
          },
        ]}
      />
      <p>
        You can manage external Dagster Transform applications from this screen.
      </p>

      <S2Typography
        variant="headline-4"
        translatedContent="Dagster Transforms"
      />
      <div className="flex flex-col items-start">
        <Button
          behavior={{
            type: 'onClick',
            onClick: openCreateModal,
          }}
          text="New Dagster Transform"
        />
      </div>

      {errorMessage && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded text-red-700">
          {errorMessage}
        </div>
      )}

      <Table
        items={localTransforms}
        columnDefinitions={columnDefinitions}
        isLoading={transformsReadPattern.status === 'loading'}
        empty={
          <div className="text-center py-8 text-gray-500">
            No dagster transforms found
          </div>
        }
      />

      <Transition
        appear
        show={isCreateModalOpen || isEditModalOpen}
        as={Fragment}
      >
        <Dialog as="div" className="relative z-10" onClose={closeModals}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    {isCreateModalOpen
                      ? 'Create Dagster Transform'
                      : 'Edit Dagster Transform'}
                  </Dialog.Title>

                  <div className="mt-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Name
                      </label>
                      <TextInput
                        value={name}
                        onChange={(e) => setName(e.currentTarget.value)}
                        placeholder="Enter transform name"
                        className="w-full mt-1"
                        type="text"
                        data-qa="transformNameInput"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Database
                      </label>
                      <TextInput
                        value={database}
                        onChange={(e) => setDatabase(e.currentTarget.value)}
                        placeholder="Enter Snowflake database name"
                        className="w-full mt-1"
                        type="text"
                        data-qa="databaseInput"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Schema
                      </label>
                      <TextInput
                        value={schema}
                        onChange={(e) => setSchema(e.currentTarget.value)}
                        placeholder="Enter Snowflake schema name"
                        className="w-full mt-1"
                        type="text"
                        data-qa="schemaInput"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Table Name
                      </label>
                      <TextInput
                        value={tableName}
                        onChange={(e) => setTableName(e.currentTarget.value)}
                        placeholder="Enter table name"
                        className="w-full mt-1"
                        type="text"
                        data-qa="tableNameInput"
                      />
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end gap-2">
                    <Button
                      behavior={{
                        type: 'onClick',
                        onClick: closeModals,
                      }}
                      text="Cancel"
                      variant="outline"
                    />
                    <Button
                      behavior={{
                        type: 'onClick',
                        onClick: isCreateModalOpen
                          ? createTransform
                          : updateTransform,
                      }}
                      text={isCreateModalOpen ? 'Create' : 'Update'}
                      disabled={
                        isCreating ||
                        isUpdating ||
                        !name ||
                        !database ||
                        !schema ||
                        !tableName
                      }
                    />
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <Transition appear show={isGrantAccessModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModals}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Manage Access: {selectedTransformForAccess?.name}
                  </Dialog.Title>

                  <div className="mt-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Grant Access to Organization
                      </label>
                      {organizationsReadPattern.status === 'done' &&
                        'entity' in organizationsReadPattern && (
                          <select
                            value={selectedOrganizationId}
                            onChange={(e) =>
                              setSelectedOrganizationId(e.target.value)
                            }
                            className="form-select mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                          >
                            <option value="">Select an organization...</option>
                            {organizationsReadPattern.entity.organizations.map(
                              (org: { id: string; name: string }) => (
                                <option key={org.id} value={org.id}>
                                  {org.name}
                                </option>
                              ),
                            )}
                          </select>
                        )}
                      <div className="mt-2">
                        <Button
                          behavior={{
                            type: 'onClick',
                            onClick: grantAccess,
                          }}
                          text="Grant Access"
                          disabled={isGranting || !selectedOrganizationId}
                          size="small"
                        />
                      </div>
                    </div>

                    <div className="mt-6">
                      <S2Typography
                        variant="headline-6-alt"
                        translatedContent="Organizations with Access"
                      />
                      <Table
                        items={localEntitlements}
                        columnDefinitions={entitlementsColumnDefinitions}
                        isLoading={false}
                        empty={
                          <div className="text-center py-4 text-gray-500">
                            No organizations have access yet
                          </div>
                        }
                      />
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end">
                    <Button
                      behavior={{
                        type: 'onClick',
                        onClick: closeModals,
                      }}
                      text="Close"
                    />
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </AdminLayout>
  );
};
